import {
  isWeekend as dateFnsIsWeekend,
  addDays,
  subDays,
  isSameDay,
  parseISO,
  startOfDay,
  differenceInMinutes
} from "date-fns";

export const isWeekend = date => dateFnsIsWeekend(date);

export const isBusinessDay = date => !isWeekend(date);

export const calculateWorkingDaysOff = (lastDayOfWork, returnToWorkDate) => {
  if (!lastDayOfWork || !returnToWorkDate) return "";

  const lastDay = startOfDay(parseISO(lastDayOfWork));
  const returnDay = startOfDay(parseISO(returnToWorkDate));

  if (returnDay <= lastDay) return "";

  let count = 0;
  let current = addDays(lastDay, 1);
  const end = addDays(returnDay, -1);

  while (current <= end) {
    if (!isWeekend(current)) {
      count += 1;
    }
    current = addDays(current, 1);
  }

  return count > 0 ? count : "";
};

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
const WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

export const DEFAULT_WORKING_START = "08:00"
export const DEFAULT_WORKING_FINISH = "17:00"

export const DEFAULT_WORKING_HOURS_SCHEDULE = WEEKDAY_KEYS.reduce((schedule, day) => {
  schedule[day] = {
    works: day !== "saturday" && day !== "sunday",
    start: DEFAULT_WORKING_START,
    finish: DEFAULT_WORKING_FINISH
  }
  return schedule
}, {})

function dayNameFromDate(date) {
  return DAY_NAMES[date.getDay()]
}

function parseTimeOnDate(baseDate, timeStr) {
  const [hours, minutes] = timeStr.split(":").map(Number)
  const result = new Date(baseDate)
  result.setHours(hours, minutes || 0, 0, 0)
  return result
}

function maxDate(a, b) {
  return a > b ? a : b
}

function minDate(a, b) {
  return a < b ? a : b
}

export function buildWorkingHoursScheduleFromUser(attributes = {}) {
  return WEEKDAY_KEYS.reduce((schedule, day) => {
    const camelDay = day.charAt(0).toUpperCase() + day.slice(1)
    const works = attributes[day] ?? attributes[camelDay]
    const worksDefault = day !== "saturday" && day !== "sunday"
    const start = attributes[`${day}StartTime`] || attributes[`${day}_start_time`] || DEFAULT_WORKING_START
    const finish = attributes[`${day}FinishTime`] || attributes[`${day}_finish_time`] || DEFAULT_WORKING_FINISH

    schedule[day] = {
      works: works !== undefined ? !!works : worksDefault,
      start,
      finish
    }
    return schedule
  }, {})
}

export function getWorkingDayScheduleForDate(date, schedule = DEFAULT_WORKING_HOURS_SCHEDULE) {
  return schedule[dayNameFromDate(date)]
}

export function findDefaultLeaveDate(schedule = DEFAULT_WORKING_HOURS_SCHEDULE) {
  let candidate = new Date()

  for (let i = 0; i < 14; i += 1) {
    const daySchedule = getWorkingDayScheduleForDate(candidate, schedule)
    if (daySchedule?.works) return new Date(candidate)
    candidate = addDays(candidate, 1)
  }

  return new Date()
}

export function buildDefaultLeavePeriod(schedule = DEFAULT_WORKING_HOURS_SCHEDULE, baseDate = findDefaultLeaveDate(schedule)) {
  const daySchedule = getWorkingDayScheduleForDate(baseDate, schedule) || {
    start: DEFAULT_WORKING_START,
    finish: DEFAULT_WORKING_FINISH
  }

  return {
    startAt: parseTimeOnDate(baseDate, daySchedule.start),
    endAt: parseTimeOnDate(baseDate, daySchedule.finish)
  }
}

function parseTimeParts(timeStr) {
  const [hours, minutes] = (timeStr || DEFAULT_WORKING_FINISH).split(":").map(Number)
  return { hours, minutes: minutes || 0 }
}

export function finishTimeForDate(date, schedule = DEFAULT_WORKING_HOURS_SCHEDULE) {
  const daySchedule = getWorkingDayScheduleForDate(date, schedule)
  return parseTimeParts(daySchedule?.finish || DEFAULT_WORKING_FINISH)
}

export function calculateWorkingHoursBetween(start, end, schedule = DEFAULT_WORKING_HOURS_SCHEDULE) {
  if (!start || !end || end <= start) return 0

  let totalMinutes = 0
  let current = startOfDay(start)
  const lastDay = startOfDay(end)

  while (current <= lastDay) {
    const daySchedule = schedule[dayNameFromDate(current)]

    if (daySchedule?.works) {
      const workStart = parseTimeOnDate(current, daySchedule.start)
      const workEnd = parseTimeOnDate(current, daySchedule.finish)
      const overlapStart = maxDate(start, workStart)
      const overlapEnd = minDate(end, workEnd)

      if (overlapEnd > overlapStart) {
        totalMinutes += differenceInMinutes(overlapEnd, overlapStart)
      }
    }

    current = addDays(current, 1)
  }

  return totalMinutes / 60
}

function analyzeLeaveWorkingTime(start, end, schedule) {
  let fullDays = 0
  let partialHours = 0
  let current = startOfDay(start)
  const lastDay = startOfDay(end)

  while (current <= lastDay) {
    const daySchedule = schedule[dayNameFromDate(current)]

    if (daySchedule?.works) {
      const workStart = parseTimeOnDate(current, daySchedule.start)
      const workEnd = parseTimeOnDate(current, daySchedule.finish)
      const overlapStart = maxDate(start, workStart)
      const overlapEnd = minDate(end, workEnd)
      const fullDayMinutes = differenceInMinutes(workEnd, workStart)

      if (overlapEnd > overlapStart && fullDayMinutes > 0) {
        const overlapMinutes = differenceInMinutes(overlapEnd, overlapStart)

        if (overlapMinutes >= fullDayMinutes - 1) {
          fullDays += 1
        } else {
          partialHours += overlapMinutes / 60
        }
      }
    }

    current = addDays(current, 1)
  }

  return { fullDays, partialHours }
}

export const formatLeaveDaysOff = (startAt, endAt, workingDaysOff, workingHoursSchedule = DEFAULT_WORKING_HOURS_SCHEDULE) => {
  if (startAt && endAt) {
    const start = parseISO(startAt)
    const end = parseISO(endAt)

    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const { fullDays, partialHours } = analyzeLeaveWorkingTime(start, end, workingHoursSchedule)

      if (fullDays > 0 && partialHours === 0) {
        return fullDays
      }

      if (fullDays === 0 && partialHours > 0) {
        const rounded = Math.round(partialHours * 10) / 10
        return rounded === 1 ? "1 hour" : `${rounded} hours`
      }

      if (fullDays > 0 && partialHours > 0) {
        return workingDaysOff ?? fullDays
      }
    }
  }

  if (workingDaysOff === null || workingDaysOff === undefined || workingDaysOff === "") {
    return "—"
  }

  return workingDaysOff
};

export const isPastDate = date => startOfDay(date) < startOfDay(new Date());

export const isValidBookingDate = (date, options = {}) => {
  const {
    allowPast = false,
    allowWeekends = false,
    excludeDates = [],
    publicHolidays = []
  } = options;

  if (!allowPast && isPastDate(date)) {
    return false;
  }

  if (!allowWeekends && isWeekend(date)) {
    return false;
  }

  if (excludeDates.some(excludeDate => isSameDay(date, excludeDate))) {
    return false;
  }

  if (publicHolidays.some(holiday => isSameDay(date, holiday))) {
    return false;
  }

  return true;
};

export const getNextBusinessDay = (startDate, options = {}) => {
  const { excludeDates = [], publicHolidays = [] } = options;
  let nextDay = addDays(startDate, 1);

  while (!isValidBookingDate(nextDay, {
    allowPast: true,
    allowWeekends: false,
    excludeDates,
    publicHolidays
  })) {
    nextDay = addDays(nextDay, 1);
  }

  return nextDay;
};

export const generateExcludedDates = (options = {}) => {
  const {
    includePastDates = true,
    includeWeekends = true,
    customExcludeDates = [],
    publicHolidays = [],
    futureDays = 90
  } = options;

  const excludedDates = [];
  const today = new Date();

  if (includePastDates) {
    for (let i = 1; i <= 365; i += 1) {
      excludedDates.push(subDays(today, i));
    }
  }

  if (includeWeekends) {
    for (let i = 0; i <= futureDays; i += 1) {
      const checkDate = addDays(today, i);
      if (isWeekend(checkDate)) {
        excludedDates.push(checkDate);
      }
    }
  }

  excludedDates.push(...customExcludeDates, ...publicHolidays);

  return excludedDates;
};

export const createDateFilter = (options = {}) => date => isValidBookingDate(date, options);

export const getMinSelectableDate = (customMinDate = null) => {
  if (customMinDate) {
    return new Date(Math.max(customMinDate.getTime(), new Date().getTime()));
  }
  return new Date();
};

export const getDateValidationError = (selectedDate, options = {}) => {
  const {
    allowPast = false,
    allowWeekends = false
  } = options;

  if (!allowPast && isPastDate(selectedDate)) {
    return "You can only select dates in the future. Please choose an available timeslot.";
  }

  if (!allowWeekends && isWeekend(selectedDate)) {
    return "Weekend dates are not available for booking. Please select a weekday.";
  }

  return "This date is not available for booking. Please select a different date.";
};

export const DEFAULT_BOOKING_OPTIONS = {
  allowPast: false,
  allowWeekends: false,
  excludeDates: [],
  publicHolidays: []
};

export const VALIDATION_PRESETS = {
  BOOKING_DATES: {
    allowPast: false,
    allowWeekends: true
  },

  STAFF_LEAVE: {
    allowPast: false,
    allowWeekends: true
  },

  TIME_TRACKING: {
    allowPast: true,
    allowWeekends: true
  },

  REPORTS: {
    allowPast: true,
    allowWeekends: true
  }
};
